import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/hooks/use-auth'
import { isTokenAuthMode } from '@/auth/auth-mode'
import { StagingTokenLoginCard } from '@/auth/TokenAuthProvider'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { LogoIcon } from '@/assets/Logo'

export const Route = createFileRoute('/login')({
  staticData: { title: 'Sign in' },
  component: LoginPage,
})

// Session-scoped flag. Set when we kick off a prompt=none redirect, so that
// if it returns with an error we don't loop. Cleared when the user reaches
// /dashboard authenticated.
const SILENT_SSO_ATTEMPTED_KEY = 'msal:silent-sso-attempted'

function hasAttemptedSilentSso(): boolean {
  try {
    return sessionStorage.getItem(SILENT_SSO_ATTEMPTED_KEY) === '1'
  } catch {
    // sessionStorage can throw in locked-down contexts; treat as "yes, attempted"
    // so we don't redirect-loop when we can't track state.
    return true
  }
}

function markSilentSsoAttempted(): void {
  try {
    sessionStorage.setItem(SILENT_SSO_ATTEMPTED_KEY, '1')
  } catch {
    // ignore — worst case we show the button immediately
  }
}

function LoginPage() {
  const { signIn, silentSignIn, isLoading, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  // Lazy init: snapshot once on mount. True if this tab's session hasn't yet
  // attempted a silent SSO redirect; stays true while that redirect is in
  // flight, flips to false only if the redirect returns with an error.
  const [silentAttemptPending, setSilentAttemptPending] = useState(
    () => !hasAttemptedSilentSso(),
  )

  useEffect(() => {
    if (isLoading) return
    if (isAuthenticated) {
      navigate({ to: '/dashboard' })
      return
    }
    if (!silentAttemptPending) return
    markSilentSsoAttempted()
    silentSignIn().catch(() => {
      setSilentAttemptPending(false)
    })
  }, [isLoading, isAuthenticated, navigate, silentSignIn, silentAttemptPending])

  const showSpinner = isLoading || silentAttemptPending

  if (isTokenAuthMode) {
    if (isAuthenticated) {
      return (
        <div className="flex min-h-screen items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )
    }

    return (
      <StagingTokenLoginCard
        isLoading={isLoading}
        onSubmit={async (token) => {
          await signIn(token)
          navigate({ to: '/dashboard' })
        }}
      />
    )
  }

  if (showSpinner) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <LogoIcon />
          </div>
          <CardTitle className="text-xl">USA-RS Dashboard</CardTitle>
          <CardDescription>Sign in with your Microsoft account to continue</CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            className="w-full"
            onClick={() => signIn()}
            disabled={isLoading}
          >
            Sign in with Microsoft
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
